import { HttpClient } from '@angular/common/http';
import { Subject, Observable, AsyncSubject } from 'rxjs';
import { share } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Constants } from './constants';
import { IdentityRepository, IdentityUser, Menu } from '@model/identity';
import { IdentityOperations } from '@model/identity/operations';
import { dateWithTimeToString, fromMills } from '@common/date-utils';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';

/**
 * Implementación del servicio de identidad
 */
@Injectable({
  providedIn: 'root'
})
export class IdentityServiceImpl extends IdentityRepository {
  private appConfig: AppConfig = inject(APP_CONFIG);

  /** representa el usuario logueado en el sistema */
  private user?: IdentityUser = undefined;

  /** Subject para notificar la informacion del usuario  */
  private authState = new Subject<IdentityUser>();

  /**
   *  Almacena una referencia hacia el observable que obtiene la información del usuario y lo marca de tipo share para que
   *  no se haga una misma peticion multiples veces
   */
  private observerLoginInfo?: Observable<IdentityUser> = undefined;

  /**
   * servicio para hacer peticiones http
   */
  private readonly http = inject(HttpClient);

  /**
   * Esta función se encarga de enviar por el authState el usuario, ya sea que lo recupere del sessionStorage o del servicio
   * expuesto en el backend
   */
  refreshUserInfo(): void {
    this.observerLoginInfo = this.http
      .get<IdentityUser>(
        this.appConfig.backend.context + Constants.securityUserInfo
      )
      .pipe(
        share({
          connector: () => new AsyncSubject(),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false
        })
      );

    this.observerLoginInfo.subscribe((user) => {
      this.user = user;
      this.user.lastAccess = dateWithTimeToString(
        fromMills(Number(user.lastAccess))
      );
      if (this.user) {
        this.authState.next(user);
      }
    });
  }

  /**
   * Devuelve un Observable en el cual se notificará el usuario en la sesión.
   *
   * @returns devuelve un stream donde se puede obtener la información del usuario logueado
   */
  getUserInfo(): Observable<IdentityUser> {
    if (!this.observerLoginInfo) {
      this.refreshUserInfo();
    }

    return this.observerLoginInfo as Observable<IdentityUser>;
  }

  /**
   * Devuelve el menu asociado a el usuario
   *
   * @returns devuelve el menu del usuario
   */
  getUserMenu(): Promise<Menu[]> {
    return new Promise((resolver) => {
      if (this.user != null) {
        resolver(IdentityOperations.getMenu(this.user));
      } else {
        this.getUserInfo().subscribe((user) => {
          resolver(IdentityOperations.getMenu(user));
        });
      }
    });
  }

  /**
   * Con esta funcion se puede obtener el valor LoggedInYet o el formulario de login enviado por el sistema de seguridad
   *
   * @returns devuelve el html que representa el formulario de login
   */
  getLoginForm(): Observable<string> {
    const options = { responseType: 'text' as const };
    return this.http.post(
      this.appConfig.backend.context + Constants.securityCheckStatus,
      null,
      options
    );
  }

  /**
   * Esta funcion comprueba si un usuario esta logueado en el sistema
   *
   * @returns indica si el usuario esta logueado en el sistema o no
   */
  isLoggedIn(): boolean {
    return true;
  }

  /**
   * Con esta funcion se puede comprobar si el usuario en la sesión tiene permiso hacia un recurso especifico
   *
   * @param resource url del recurso
   * @returns indica si un usuario tiene acceso a un recurso específico
   */
  isAuthorized(resource: string): Observable<boolean> {
    const body = {
      method: 'GET',
      path: resource
    };
    return this.http.post<boolean>(
      this.appConfig.backend.context + Constants.securityUserAccess,
      body
    );
  }

  /**
   * Esta funcion invoca el logout completo del sistema, aqui si se cierra la sesión y el usuario debería ingresar sus
   * credenciales si desea volver a ingresar.
   */
  fullLogout(): void {
    const options = { responseType: 'text' as const };
    this.http
      .get(this.appConfig.backend.context + Constants.securityLogout, options)
      .pipe(
        share({
          connector: () => new AsyncSubject(),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false
        })
      )
      .subscribe((endpoint) => {
        window.location.href = endpoint;
      });
  }

  /**
   * Esta función se encarga de eliminar la referencia del Observable que devuelve la información del usuario logueado
   * de esta forma se puede hacer refresh de dicha información solicitandola al backend
   */
  clearObserverForLogin(): void {
    this.observerLoginInfo = undefined;
    this.user = undefined;
  }

  /**
   * Limpia la sesión almacenada localmente (localStorage)
   */
  clearLocalSession(): void {
    try {
      localStorage.removeItem('mur_logged_user');
    } catch (error) {
      console.error('Error al limpiar la sesión local:', error);
    }
  }
}
